"use client";

import { useState } from "react";
import Link from "next/link";
import Image, { StaticImageData } from "next/image";
import { ArrowUpRight } from "lucide-react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import { services, landingServices } from "@/constants/services";
import { Section } from "./ui/section";
import { SectionHeader } from "./ui/section-header";
import { EASE } from "@/lib/motion";

const listVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};

const rowVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease: EASE } },
};

type Item = {
  key: string;
  title: string;
  subtitle: string;
  href: string;
  previewImage?: StaticImageData;
};

type Props = {
  landing?: boolean;
};

export function Services({ landing = false }: Props) {
  const items: Item[] = landing
    ? landingServices.map((s, i) => {
        const slug = s.href.split("/").pop() ?? "";
        const src = services[slug];
        return {
          key: `landing-${i}`,
          title: s.title,
          subtitle: s.subtitle,
          href: s.href,
          previewImage: src?.card_1?.image ?? src?.before_after?.afterImage,
        };
      })
    : Object.entries(services)
        .slice(1)
        .map(([key, s]) => ({
          key,
          title: s.title,
          subtitle: s.subtitle,
          href: `/usluge/${key}`,
          previewImage: s.card_1?.image ?? s.before_after?.afterImage,
        }));

  const [active, setActive] = useState(0);
  const total = items.length;

  return (
    <Section className="bg-gradient-to-b from-transparent via-brand-50/15 to-transparent">
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 lg:gap-12 mb-8 lg:mb-12">
        <SectionHeader
          eyebrow="Specijalistička područja"
          title={
            <>
              Svaki zahvat izvodi se uz maksimalnu kontrolu{" "}
              <span className="font-serif italic font-normal tracking-[-0.02em] text-brand-800">
                detalja
              </span>
              , premium materijale i vrhunsku opremu.
            </>
          }
          className="max-w-2xl"
        />

        {landing && (
          <Link
            href="/usluge"
            aria-label="Sve usluge"
            className="group inline-flex items-center gap-3 self-start lg:self-auto text-xs uppercase tracking-[0.2em] text-foreground/80 hover:text-foreground transition-colors"
          >
            Sve usluge
            <span className="relative grid place-items-center size-10 rounded-full border border-brand-800/25 overflow-hidden transition-colors duration-500 group-hover:border-brand-800">
              <span
                aria-hidden
                className="absolute inset-0 origin-bottom scale-y-0 bg-brand-800 transition-transform duration-500 ease-[cubic-bezier(0.6,-0.05,0.01,0.99)] group-hover:scale-y-100"
              />
              <ArrowUpRight className="relative size-4 text-brand-800 transition-colors duration-500 group-hover:text-background" />
            </span>
          </Link>
        )}
      </header>

          <div
            className={cn(
              "grid items-stretch gap-8 lg:gap-12 xl:gap-16",
              landing ? "lg:grid-cols-[1fr_auto]" : "grid-cols-1",
            )}
          >
            <motion.ul
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={listVariants}
              onMouseLeave={landing ? () => setActive(0) : undefined}
              className="flex flex-col"
            >
              {items.map((item, i) => (
                <Row
                  key={item.key}
                  index={i}
                  item={item}
                  isActive={landing ? active === i : false}
                  onHover={landing ? () => setActive(i) : undefined}
                  full={!landing}
                />
              ))}
              <motion.div
                variants={rowVariants}
                aria-hidden
                className="h-px bg-gradient-to-r from-brand-300/45 via-brand-300/20 to-transparent"
              />
            </motion.ul>

            {landing && (
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
                className="hidden lg:block w-[340px] xl:w-[380px] h-full"
              >
                <div className="relative h-full min-h-[400px] overflow-hidden bg-brand-100 ring-1 ring-brand-300/30">
                  {items.map((item, i) =>
                    item.previewImage ? (
                      <Image
                        key={item.key}
                        src={item.previewImage}
                        alt=""
                        fill
                        placeholder="blur"
                        className={cn(
                          "object-cover will-change-transform transition-all duration-[900ms] ease-[cubic-bezier(0.6,-0.05,0.01,0.99)]",
                          active === i
                            ? "opacity-100 scale-100"
                            : "opacity-0 scale-[1.05]",
                        )}
                      />
                    ) : null,
                  )}

                  <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-t from-brand-900/55 via-brand-900/10 to-transparent z-10 pointer-events-none"
                  />

                  <div className="absolute inset-x-0 bottom-0 z-20 px-7 py-7 flex items-end justify-between gap-4">
                    <div className="flex flex-col gap-1.5 min-w-0">
                      <span className="font-serif italic text-background/80 text-sm leading-none">
                        {String(active + 1).padStart(2, "0")}
                        <span className="text-background/45">
                          {" "}
                          / {String(total).padStart(2, "0")}
                        </span>
                      </span>
                      <span className="text-background text-[19px] leading-[1.15] tracking-[-0.025em] line-clamp-2">
                        {items[active]?.title}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
    </Section>
  );
}

type RowProps = {
  index: number;
  item: Item;
  isActive: boolean;
  onHover?: () => void;
  full?: boolean;
};

function Row({ index, item, isActive, onHover, full }: RowProps) {
  const num = String(index + 1).padStart(2, "0");

  return (
    <motion.li
      variants={rowVariants}
      onMouseEnter={onHover}
      className="relative"
    >
      <Link
        href={item.href}
        className={cn(
          "group/row relative flex items-start gap-4 md:gap-6 border-t border-brand-300/40",
          full ? "py-7 md:py-9" : "py-5 md:py-6",
        )}
      >
        <span
          aria-hidden
          className={cn(
            "absolute top-0 left-0 right-0 h-px origin-left bg-brand-800 transition-transform duration-700 ease-[cubic-bezier(0.6,-0.05,0.01,0.99)]",
            isActive ? "scale-x-100" : "scale-x-0",
            full && "group-hover/row:scale-x-100",
          )}
        />

        <span
          aria-hidden
          className={cn(
            "shrink-0 font-serif italic transition-all duration-500 ease-out",
            full
              ? "w-10 md:w-12 pt-1 md:pt-1.5 text-2xl md:text-[28px]"
              : "w-8 md:w-10 pt-0.5 md:pt-1 text-xl md:text-2xl",
            isActive
              ? "text-brand-800 translate-x-0"
              : "text-brand-800/40 -translate-x-0.5",
            full &&
              "group-hover/row:text-brand-800 group-hover/row:translate-x-0",
          )}
        >
          {num}
        </span>

        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <h3
            className={cn(
              "leading-[1.15] tracking-[-0.03em] transition-all duration-500 ease-out",
              full
                ? "text-2xl sm:text-3xl lg:text-[32px]"
                : "text-2xl sm:text-3xl",
              isActive ? "text-foreground translate-x-1" : "text-foreground/55",
              full &&
                "group-hover/row:text-foreground group-hover/row:translate-x-1",
            )}
          >
            {item.title}
          </h3>
          <p
            className={cn(
              "leading-[1.5] tracking-[-0.005em] transition-all duration-500 ease-out line-clamp-2",
              full
                ? "text-sm md:text-[15px] max-w-[640px]"
                : "text-[13px] md:text-sm max-w-[520px]",
              isActive
                ? "text-foreground/65 opacity-100"
                : "text-foreground/45 opacity-80",
              full &&
                "group-hover/row:text-foreground/65 group-hover/row:opacity-100",
            )}
          >
            {item.subtitle}
          </p>
        </div>

        <span
          aria-hidden
          className={cn(
            "shrink-0 hidden md:grid place-items-center mt-1 rounded-full border transition-all duration-500 ease-out",
            full ? "size-11" : "size-9",
            isActive
              ? "border-brand-800 bg-brand-800"
              : "border-brand-800/20 bg-transparent",
            full &&
              "group-hover/row:border-brand-800 group-hover/row:bg-brand-800",
          )}
        >
          <ArrowUpRight
            className={cn(
              "size-4 transition-all duration-500 ease-out",
              isActive
                ? "text-background -translate-y-px translate-x-px"
                : "text-brand-800/55",
              full &&
                "group-hover/row:text-background group-hover/row:-translate-y-px group-hover/row:translate-x-px",
            )}
          />
        </span>
      </Link>
    </motion.li>
  );
}
